/**
 * Eraser power-up shop.
 *
 * Visual structure:
 *   - Gold-tinted balance hero card with the inventory count.
 *   - Gold-bordered explainer strip.
 *   - "Buy erasers" list with three bundles; the cheapest-per-unit one
 *     gets a "POPULAR" badge + outlined card. Each non-singleton bundle
 *     shows its savings vs. the 1-pack unit price.
 *   - Footer note pointing out that erasers can also be earned by
 *     ranking inside a quiz's reward range.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

const ERASER_SVG = `
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M20.71 14.29l-7-7a1 1 0 0 0-1.42 0L3.71 15.88a1 1 0 0 0 0 1.41l4.79 4.79a1 1 0 0 0 1.41 0L20.71 11.7a1 1 0 0 0 0-1.41z"></path>
    <path d="M14 22H7"></path>
  </svg>
`;

export async function renderErasers(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/wallet" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Erasers</div>
    </div>
    <div class="pad" id="erasers-body">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const host = document.getElementById('erasers-body');
  let products;
  let balance;
  let wallet;
  try {
    // Pulled in the same fan-out as the catalog/inventory because the
    // bonus-credit nudge is part of the page's first paint — pulling
    // it separately would either flash empty or block the whole render.
    const [bal, prods, w] = await Promise.all([
      api('/erasers/balance'),
      api('/erasers/products'),
      api('/wallet'),
    ]);
    balance = bal.eraser_balance;
    products = prods.items;
    wallet = w;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  // Cheapest per-unit bundle wins the "POPULAR" badge — call it out and
  // outline the card. Singletons never qualify.
  const popularId = products
    .filter((p) => p.count > 1)
    .reduce(
      (best, p) => (best == null || p.unit_price_kobo < best.unit_price_kobo ? p : best),
      null,
    )?.id;

  // The single-unit price drives every savings calculation — if the
  // backend ever drops it, fall through gracefully (no badge, no savings).
  const single = products.find((p) => p.count === 1);
  const singleUnit = single ? single.price_kobo : null;

  host.innerHTML = `
    <div class="card" style="padding:24px 18px;background:radial-gradient(120% 100% at 50% 0%, rgba(229,193,0,0.18) 0%, rgba(58,42,20,0.55) 50%, rgba(21,16,43,0.85) 100%);border:1px solid rgba(229,193,0,0.35);text-align:center;">
      <div style="display:inline-flex;align-items:center;justify-content:center;width:64px;height:64px;background:rgba(229,193,0,0.10);border:1px solid rgba(229,193,0,0.40);border-radius:14px;color:#E5C100;">
        ${ERASER_SVG}
      </div>
      <div style="margin-top:14px;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:rgba(229,193,0,0.85);font-weight:700;">
        Your erasers
      </div>
      <div id="eraser-balance" style="font-family:var(--font-display);font-size:46px;font-weight:800;color:#E5C100;line-height:1.05;margin-top:6px;">
        ${balance}
      </div>
    </div>

    <div class="card" style="padding:14px 16px;margin-top:12px;background:transparent;border:1px solid rgba(229,193,0,0.35);color:#E5C100;font-size:13px;line-height:1.5;">
      An eraser automatically corrects your first wrong answer in a game.
      Choose to use one when you join.
    </div>

    <div id="eraser-wallet-strip" class="card" style="padding:12px 14px;margin-top:10px;">
      <div class="row" style="gap:12px;align-items:center;">
        <div class="grow" style="min-width:0;">
          <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">Wallet</div>
          <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.15;margin-top:2px;" id="eraser-wallet-cash">
            ${escape(formatKobo(wallet.balance_kobo))}
          </div>
        </div>
        <div id="eraser-wallet-bonus" style="text-align:right;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#E94BA2;font-weight:700;">🎁 Bonus</div>
          <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.15;margin-top:2px;" id="eraser-wallet-bonus-amount">—</div>
        </div>
      </div>
      <div id="eraser-bonus-note" class="muted" style="font-size:11px;line-height:1.45;margin-top:8px;">
        Purchases spend bonus first, then cash. Win a 1-on-1 duel to convert bonus to cash.
      </div>
    </div>

    <h3 class="sec" style="margin-top:18px;">Buy erasers</h3>
    <div id="erasers-products" style="display:grid;gap:10px;">
      ${products.map((p) => renderProductCard(p, p.id === popularId, singleUnit)).join('')}
    </div>

    <div class="muted" style="font-size:11.5px;line-height:1.5;margin-top:14px;text-align:left;">
      Paid from your wallet · You can also win erasers by finishing in
      a game's reward positions.
    </div>
  `;

  renderWalletStrip(wallet);

  document.querySelectorAll('[data-buy]').forEach((btn) => {
    btn.addEventListener('click', () => buy(btn.dataset.buy));
  });
}

function renderWalletStrip(wallet) {
  const cashEl = document.getElementById('eraser-wallet-cash');
  if (cashEl) cashEl.textContent = formatKobo(wallet.balance_kobo);
  // Bonus column + "purchases spend bonus first" note always rendered so
  // users see the second balance exists even before they've won any —
  // the note doubles as feature discovery for new players.
  const bonusAmt = document.getElementById('eraser-wallet-bonus-amount');
  if (bonusAmt) bonusAmt.textContent = formatKobo(Number(wallet.bonus_balance_kobo || 0));
}

function renderProductCard(p, isPopular, singleUnit) {
  const labelN = p.count === 1 ? '1 Eraser' : `${p.count} Erasers`;
  // Savings line: only meaningful for true bundles where the per-unit
  // price actually beats the single — guards against future SKU drift.
  let savingsKobo = 0;
  if (singleUnit && p.count > 1) {
    savingsKobo = Math.max(0, singleUnit * p.count - p.price_kobo);
  }
  const savingsLine = savingsKobo > 0
    ? `<div style="font-size:11.5px;font-weight:700;color:#2BB39B;margin-top:2px;letter-spacing:0.03em;">SAVE ${escape(formatKobo(savingsKobo))}</div>`
    : '';

  const popularBadge = isPopular
    ? `<span style="display:inline-flex;align-items:center;padding:4px 9px;background:#2A2244;border:1px solid #3a2f5e;border-radius:6px;font-size:10px;font-weight:800;color:#E5C100;letter-spacing:0.10em;font-family:var(--font-display);text-transform:uppercase;margin-right:6px;">
         Popular
       </span>`
    : '';

  const cardStyle = isPopular
    ? 'padding:14px;background:var(--surf-2);border:1.5px solid rgba(229,193,0,0.65);box-shadow:0 0 0 1px rgba(229,193,0,0.10);'
    : 'padding:14px;background:var(--surf-2);';

  return `
    <div class="card" style="${cardStyle}">
      <div class="row" style="align-items:center;gap:12px;">
        <div style="width:42px;height:42px;flex-shrink:0;background:#3a2a14;border:1px solid rgba(229,193,0,0.35);border-radius:10px;display:flex;align-items:center;justify-content:center;color:#E5C100;">
          ${ERASER_SVG}
        </div>
        <div class="grow" style="min-width:0;">
          <div style="font-size:16px;color:#fff;font-weight:700;">${escape(labelN)}</div>
          ${savingsLine}
        </div>
        ${popularBadge}
        <button type="button" class="btn sm gold" data-buy="${escape(p.id)}"
                style="white-space:nowrap;min-width:88px;text-align:center;font-weight:800;">
          ${escape(formatKobo(p.price_kobo))}
        </button>
      </div>
    </div>
  `;
}

async function buy(productId) {
  const btn = document.querySelector(`[data-buy="${CSS.escape(productId)}"]`);
  if (!btn) return;
  if (!confirm('Purchase erasers? Your wallet will be charged.')) return;
  btn.disabled = true;
  const original = btn.textContent;
  btn.textContent = 'Buying…';
  try {
    const idempotency_key = `er-buy-${productId}-${crypto.randomUUID()}`;
    const res = await api('/erasers/purchase', {
      method: 'POST',
      body: { product_id: productId, idempotency_key },
    });
    document.getElementById('eraser-balance').textContent = res.eraser_balance;
    toast.success(`Got ${res.count} eraser${res.count === 1 ? '' : 's'}!`);
    const me = auth.getMe();
    if (me) auth.setMe({ ...me, eraser_balance: res.eraser_balance });
    // Refresh the wallet strip so the bonus-drains-first behaviour is
    // visible: if the user had ₦300 bonus and just spent ₦100, the
    // strip should now read ₦200 bonus + unchanged cash.
    try {
      const w = await api('/wallet');
      renderWalletStrip(w);
    } catch {
      // Soft-fail: the purchase succeeded; a stale wallet strip is just
      // a cosmetic miss and the next page load will catch it.
    }
  } catch (err) {
    toast.fromError(err);
  } finally {
    btn.disabled = false;
    btn.textContent = original;
  }
}
